import { apiService } from './apiService';

export type VisitEventType = 'arrival' | 'departure';
export type VisitPointType = 'loading' | 'unloading' | 'intermediate' | 'delivery';

export interface PointVisitLogRow {
  id: number;
  shipment_id: number;
  shipment_no: string | null;
  order_code: string | null;
  sevk_kodu: string | null;
  plan_kodu: string | null;
  route_id: number | null;
  point_key: string;
  point_type: VisitPointType;
  point_name: string | null;
  point_latitude: number | null;
  point_longitude: number | null;
  driver_id: number | null;
  driver_name: string | null;
  driver_phone: string | null;
  plate: string | null;
  owner_type: 'own' | 'spot';
  location_source: string;
  geofence_km: number | null;
  event_type: VisitEventType;
  event_at: string | null;
  distance_km: number | null;
  latitude: number | null;
  longitude: number | null;
  created_at: string | null;
  pickup_name: string | null;
  pickup_city: string | null;
  delivery_name: string | null;
  delivery_city: string | null;
  waiting_record_id?: number | null;
  waiting_reason?: string | null;
  waiting_notes?: string | null;
  waiting_reported_at?: string | null;
  waiting_ticket_status?: 'open' | 'in-progress' | 'resolved' | null;
  waiting_resolved_at?: string | null;
  waiting_driver_name?: string | null;
  waiting_location?: string | null;
}

export interface PointVisitSummaryRow {
  shipment_id: number;
  point_key: string;
  point_type: VisitPointType;
  point_name: string | null;
  point_latitude: number | null;
  point_longitude: number | null;
  shipment_code: string;
  plate: string | null;
  driver_name: string | null;
  pickup_name: string | null;
  loading_arrival_at: string | null;
  loading_departure_at: string | null;
  first_unloading_arrival_at: string | null;
  last_unloading_departure_at: string | null;
  process_status: string | null;
  owner_type: 'own' | 'spot' | null;
}

export interface PointVisitScanResult {
  scanned: number;
  logged: number;
  geofenceKm: number;
  exitKm?: number;
  updated?: number;
}

export interface PointVisitCleanupResult {
  scanned: number;
  removed: number;
  backfilled: number;
}

export interface PointVisitProblemReplyResult {
  waitingRecordId: number;
  threadId: number;
  messageId: number;
  ticketStatus: 'in-progress' | 'resolved';
}

export interface OwnFleetBackfillResult {
  shipmentId: number | null;
  transitionDate: string;
  scannedShipments: number;
  totalLocationLogs: number;
  logged: number;
  skipped: number;
  shipmentResults: Array<{
    shipmentId: number;
    shipmentNo: string;
    plate: string;
    matchedPlate: string | null;
    locationCount: number;
    logsWritten: number;
    pointDebug: Array<{
      pointKey: string;
      pointType: VisitPointType;
      pointSource: string | null;
      pointName: string;
      latitude: number | null;
      longitude: number | null;
      geofenceKm: number;
      overrideGeofenceKm: number | null;
      shipmentDeliveryLatitude: number | null;
      shipmentDeliveryLongitude: number | null;
      deliveryCoordinateDiffKm: number | null;
      nearestLocation: {
        dataTime: string | null;
        plate: string | null;
        latitude: number | null;
        longitude: number | null;
        distanceKm: number;
      } | null;
    }>;
    dayStats: Array<{
      date: string;
      startDateTime: string;
      endDateTime: string;
      locationCount: number;
      arrivalsWritten: number;
      departuresWritten: number;
      matchedPlate: string | null;
    }>;
  }>;
  cleanup: PointVisitCleanupResult;
}

export const pointVisitLogService = {
  async getLogs(params?: {
    from?: string;
    to?: string;
    plate?: string;
    orderCode?: string;
    pointName?: string;
    eventType?: VisitEventType;
    ownerType?: 'own' | 'spot';
    driverName?: string;
    phone?: string;
    pointType?: VisitPointType;
    limit?: number;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.from) queryParams.append('from', params.from);
    if (params?.to) queryParams.append('to', params.to);
    if (params?.plate) queryParams.append('plate', params.plate);
    if (params?.orderCode) queryParams.append('orderCode', params.orderCode);
    if (params?.pointName) queryParams.append('pointName', params.pointName);
    if (params?.eventType) queryParams.append('eventType', params.eventType);
    if (params?.ownerType) queryParams.append('ownerType', params.ownerType);
    if (params?.driverName) queryParams.append('driverName', params.driverName);
    if (params?.phone) queryParams.append('phone', params.phone);
    if (params?.pointType) queryParams.append('pointType', params.pointType);
    if (params?.limit) queryParams.append('limit', String(params.limit));
    const query = queryParams.toString();
    return await apiService.get<PointVisitLogRow[]>(`/point-visit-logs${query ? `?${query}` : ''}`, { timeoutMs: 120000 });
  },

  async getSummary(params?: {
    from?: string;
    to?: string;
    plate?: string;
    orderCode?: string;
    pointName?: string;
    eventType?: VisitEventType;
    ownerType?: 'own' | 'spot';
    driverName?: string;
    phone?: string;
    pointType?: VisitPointType;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.from) queryParams.append('from', params.from);
    if (params?.to) queryParams.append('to', params.to);
    if (params?.plate) queryParams.append('plate', params.plate);
    if (params?.orderCode) queryParams.append('orderCode', params.orderCode);
    if (params?.pointName) queryParams.append('pointName', params.pointName);
    if (params?.eventType) queryParams.append('eventType', params.eventType);
    if (params?.ownerType) queryParams.append('ownerType', params.ownerType);
    if (params?.driverName) queryParams.append('driverName', params.driverName);
    if (params?.phone) queryParams.append('phone', params.phone);
    if (params?.pointType) queryParams.append('pointType', params.pointType);
    const query = queryParams.toString();
    return await apiService.get<PointVisitSummaryRow[]>(`/point-visit-logs/summary${query ? `?${query}` : ''}`, { timeoutMs: 120000 });
  },

  async runScan() {
    return await apiService.post<PointVisitScanResult>('/point-visit-logs/scan', {}, { timeoutMs: 120000 });
  },

  async cleanup() {
    return await apiService.post<PointVisitCleanupResult>('/point-visit-logs/cleanup', {}, { timeoutMs: 120000 });
  },

  async backfillOwnFleet(params?: { shipmentId?: string | number; shipmentIds?: Array<string | number> }) {
    const payload = params?.shipmentId
      ? { shipmentId: params.shipmentId }
      : params?.shipmentIds?.length
        ? { shipmentIds: params.shipmentIds }
        : {};
    return await apiService.post<OwnFleetBackfillResult>('/point-visit-logs/backfill-own-fleet', payload, { timeoutMs: 300000 });
  },

  async backfillSpotDrivers(params?: { shipmentId?: string | number; shipmentIds?: Array<string | number> }) {
    const payload = params?.shipmentId
      ? { shipmentId: params.shipmentId }
      : params?.shipmentIds?.length
        ? { shipmentIds: params.shipmentIds }
        : {};
    return await apiService.post<OwnFleetBackfillResult>('/point-visit-logs/backfill-spot-drivers', payload, { timeoutMs: 300000 });
  },

  async acceptNearestGeofence(params: { shipmentId: string | number; pointKey: string; geofenceKm: number }) {
    return await apiService.post<OwnFleetBackfillResult>('/point-visit-logs/accept-nearest-geofence', params, { timeoutMs: 300000 });
  },

  async verifyShipment(shipmentId: string | number) {
    return await apiService.get<any>(`/point-visit-logs/verify/${shipmentId}`, { timeoutMs: 300000 });
  },

  async replyToProblem(waitingRecordId: string | number, payload: { message: string; ticketStatus: 'in-progress' | 'resolved' }) {
    return await apiService.post<PointVisitProblemReplyResult>(`/waiting-records/${waitingRecordId}/reply`, payload, { timeoutMs: 120000 });
  },
};
